import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { Direction, ResizeDirection } from "abreact/incubator/ui/SharedTypes";
import { MouseMoveTracker } from "abreact/incubator/ui/utils/MouseMoveTracker";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { invokeRef, useStateWithRef } from "abreact/utils/AbHooks";
import { NumberUtils } from "abweb/utils/NumberUtils";
import * as React from "react";
// Can be absolute or relative
// If relative, need to specify which dimensions which CAN be resized
// If absolute, need to specify which dimensions CANNOT be resized
const AbResizeBox = (props, ref) => {
  const rootRef = React.useRef(null);
  const resizeDirections = propsToResizeDirections(props);
  const bailFuncRef = React.useRef(undefined);
  const [resizeStyles, setResizeStyles, resizeStylesRef] = useStateWithRef(undefined);
  const [resizeCounter, setResizeCounter] = React.useState(0);
  const startResizeWrapper = (e, direction) => {
    props.onResizeStart?.();
    bailFuncRef.current = startResize(e, direction, props.position === "absolute" && !props.onlyManageSize, rootRef, resizeStylesRef, setResizeStyles, props.onResizeEnd, () => {
      props.onResize?.();
      setResizeCounter(prev => prev + 1);
    });
  };
  const {
    children,
    ...divProps
  } = extractDivProps(props);
  React.useLayoutEffect(() => {
    bailFuncRef.current?.();
  }, [resizeCounter]);
  return /*#__PURE__*/React.createElement("div", _extends({}, divProps, {
    ref: ReactUtils.mergeRefs(rootRef, ref, element => {
      if (!props.abRef) return;
      if (!element) invokeRef(props.abRef, null);else invokeRef(props.abRef, {
        getResizeStyles: () => {
          return resizeStylesRef.current;
        },
        setResizeStyles: resizeStyles => {
          setResizeStyles(resizeStyles);
        },
        setStyleOverride: styleOverride => {
          setResizeStyles({
            ...resizeStylesRef.current,
            ...styleOverride
          });
        },
        element
      });
    }),
    className: ReactUtils.classNamesString([props.className, "ab-resize-box", resizeStyles?.height !== undefined && "ab-resize-box-user-resized-vertical", resizeStyles?.width !== undefined && "ab-resize-box-user-resized-horizontal"]),
    style: {
      ...props.style,
      position: resizeStyles ? props.position : props.style?.position,
      ...resizeStyles
    }
  }), children, resizeDirections.map(direction => /*#__PURE__*/React.createElement("div", {
    className: `ab-resize-box-${direction}-handle`,
    key: direction,
    onMouseDown: e => startResizeWrapper(e, direction)
  })));
};
const WrappedAbResizeBox = /*#__PURE__*/React.forwardRef(AbResizeBox);
export { WrappedAbResizeBox as AbResizeBox };
const extractDivProps = props => {
  if (props.position === "absolute") {
    const {
      disabled,
      onResizeStart,
      onResizeEnd,
      onResize,
      position,
      directionsToDisable,
      onlyManageSize,
      abRef,
      ...rest
    } = props;
    return rest;
  }
  const {
    disabled,
    onResizeStart,
    onResizeEnd,
    onResize,
    position,
    resizeDirections,
    abRef,
    ...rest
  } = props;
  return rest;
};
const propsToDirections = props => {
  if (props.position === "absolute") {
    return Object.values(Direction).filter(direction => !props.directionsToDisable?.includes(direction));
  }
  const directions = [];
  if (props.resizeDirections.vertical) directions.push(props.resizeDirections.vertical);
  if (props.resizeDirections.horizontal) directions.push(props.resizeDirections.horizontal);
  return directions;
};
const propsToResizeDirections = props => {
  if (props.disabled) return [];
  const directions = propsToDirections(props);
  const resizeDirections = [];
  if (directions.includes(Direction.UP)) resizeDirections.push(ResizeDirection.TOP);
  if (directions.includes(Direction.RIGHT)) resizeDirections.push(ResizeDirection.RIGHT);
  if (directions.includes(Direction.DOWN)) resizeDirections.push(ResizeDirection.BOTTOM);
  if (directions.includes(Direction.LEFT)) resizeDirections.push(ResizeDirection.LEFT);
  if (directions.includes(Direction.UP) && directions.includes(Direction.RIGHT)) resizeDirections.push(ResizeDirection.TOP_RIGHT);
  if (directions.includes(Direction.RIGHT) && directions.includes(Direction.DOWN)) resizeDirections.push(ResizeDirection.BOTTOM_RIGHT);
  if (directions.includes(Direction.DOWN) && directions.includes(Direction.LEFT)) resizeDirections.push(ResizeDirection.BOTTOM_LEFT);
  if (directions.includes(Direction.LEFT) && directions.includes(Direction.UP)) resizeDirections.push(ResizeDirection.TOP_LEFT);
  return resizeDirections;
};

// This is a function to avoid static initialization issues
const WINDOW_MARGIN = () => PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN + 5;
const directionToCursor = direction => {
  switch (direction) {
    case ResizeDirection.RIGHT:
      return "e-resize";
    case ResizeDirection.LEFT:
      return "w-resize";
    case ResizeDirection.TOP:
      return "n-resize";
    case ResizeDirection.TOP_RIGHT:
      return "ne-resize";
    case ResizeDirection.TOP_LEFT:
      return "nw-resize";
    case ResizeDirection.BOTTOM:
      return "s-resize";
    case ResizeDirection.BOTTOM_RIGHT:
      return "se-resize";
    case ResizeDirection.BOTTOM_LEFT:
      return "sw-resize";
  }
};
const resizeDirectionToDirections = direction => {
  switch (direction) {
    case ResizeDirection.RIGHT:
      return [Direction.RIGHT];
    case ResizeDirection.LEFT:
      return [Direction.LEFT];
    case ResizeDirection.TOP:
      return [Direction.UP];
    case ResizeDirection.TOP_RIGHT:
      return [Direction.UP, Direction.RIGHT];
    case ResizeDirection.TOP_LEFT:
      return [Direction.UP, Direction.LEFT];
    case ResizeDirection.BOTTOM:
      return [Direction.DOWN];
    case ResizeDirection.BOTTOM_RIGHT:
      return [Direction.DOWN, Direction.RIGHT];
    case ResizeDirection.BOTTOM_LEFT:
      return [Direction.DOWN, Direction.LEFT];
  }
};
const startResize = (e, direction, isAbsolute, rootRef, resizeStylesRef, setResizeStyles, onResizeEnd, onResize) => {
  if (!rootRef.current) return undefined;
  const boxElement = rootRef.current;
  const target = e.currentTarget;
  const cursor = directionToCursor(direction);
  const startX = e.clientX;
  const startY = e.clientY;
  const startHeight = boxElement.offsetHeight;
  const startWidth = boxElement.offsetWidth;
  const startTop = boxElement.offsetTop;
  const startBottom = startTop + startHeight;
  const startLeft = boxElement.offsetLeft;
  const startRight = startLeft + startWidth;
  const directions = resizeDirectionToDirections(direction);
  const bailFunc = () => {
    if (!isAbsolute) return;
    const bottomMoved = !NumberUtils.fuzzyEquals(boxElement.offsetTop + boxElement.offsetHeight, startBottom, 1);
    const rightMoved = !NumberUtils.fuzzyEquals(boxElement.offsetLeft + boxElement.offsetWidth, startRight, 1);
    if (!bottomMoved && !rightMoved) return;
    const newStyle = {
      ...resizeStylesRef.current
    };
    if (bottomMoved && directions.includes(Direction.UP)) {
      newStyle.top = startBottom - boxElement.offsetHeight;
    }
    if (rightMoved && directions.includes(Direction.LEFT)) {
      newStyle.left = startRight - boxElement.offsetWidth;
    }
    setResizeStyles(newStyle);
  };
  e.preventDefault();
  const tracker = new MouseMoveTracker(target);
  // dx: x distance from START of drag, dy: y distance from START of drag
  tracker.track(e.nativeEvent, (dx, dy) => {
    const newStyle = {
      ...resizeStylesRef.current
    };
    if (isAbsolute && !resizeStylesRef.current) {
      newStyle.left = startLeft;
      newStyle.top = startTop;
    }
    // Keep resize on screen
    if (startX + dx - WINDOW_MARGIN() < 0) dx = -startX + WINDOW_MARGIN();
    if (startX + dx + WINDOW_MARGIN() > boxElement.ownerDocument.documentElement.clientWidth) dx = boxElement.ownerDocument.documentElement.clientWidth - startX - WINDOW_MARGIN();
    if (startY + dy - WINDOW_MARGIN() < 0) dy = -startY + WINDOW_MARGIN();
    if (startY + dy + WINDOW_MARGIN() > boxElement.ownerDocument.documentElement.clientHeight) dy = boxElement.ownerDocument.documentElement.clientHeight - startY - WINDOW_MARGIN();
    if (directions.includes(Direction.UP)) {
      newStyle.height = startHeight - dy;
      if (isAbsolute) newStyle.top = startTop + dy;
    }
    if (directions.includes(Direction.DOWN)) {
      newStyle.height = startHeight + dy;
    }
    if (directions.includes(Direction.LEFT)) {
      newStyle.width = startWidth - dx;
      if (isAbsolute) newStyle.left = startLeft + dx;
    }
    if (directions.includes(Direction.RIGHT)) {
      newStyle.width = startWidth + dx;
    }
    setResizeStyles(newStyle);
    onResize?.();
  }, () => {
    onResizeEnd?.();
  }, cursor);
  return bailFunc;
};
//# sourceMappingURL=AbResizeBox.js.map
